import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import z from 'zod';
import { isSupabaseError, SupabaseCodeError } from '../../../../constants/supabaseErrors';
import { useSpacePath } from '../../../../hooks/useSpacePath';
import { usePostSubTopic } from '../../api/usePostSubTopic';

const formSchema = z.object({
  name: z.string().trim().min(1, 'Ce champ est obligatoire'),
});
type FormValues = z.infer<typeof formSchema>;

type Props = {
  topicId: string;
  onClose: () => void;
  onSuccess?: (subTopicId: string) => void;
};

export function useNewSubTopicModal(props: Props) {
  const navigate = useNavigate();
  const spacePath = useSpacePath();
  const postSubTopic = usePostSubTopic(props.topicId);
  const [serverError, setServerError] = useState<string | null>(null);

  const form = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: {
      name: '',
    },
    resolver: zodResolver(formSchema),
  });

  async function handleConfirm(data: FormValues) {
    setServerError(null);

    try {
      const result = await postSubTopic.mutateAsync({
        name: data.name,
        topic_id: props.topicId,
      });

      if (props.onSuccess) {
        props.onSuccess(result.id);
        props.onClose();
      } else {
        navigate(spacePath(`/topics/${result.id}`));
      }
    } catch (error: unknown) {
      if (isSupabaseError(error)) {
        setServerError(error.code === SupabaseCodeError.UniquenessViolation
          ? 'Un sous-sujet porte déjà ce nom.'
          : error.message);
        return;
      }

      setServerError('La création du sous-sujet a échoué. Réessayez.');
    }
  }

  function handleClose() {
    if (!postSubTopic.isPending) {
      props.onClose();
    }
  }

  return {
    form,
    isPending: postSubTopic.isPending,
    serverError,
    onSubmit: form.handleSubmit(handleConfirm),
    handleClose,
  };
}
